import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { MKT } from '../../Marketing/links';
const WATCH = [
    [
        'Nissan Skyline GT-R (R34)',
        '1999–2002',
        '2001 builds clear during 2026, including the M-Spec. The 2002 Nür editions clear in 2027.'
    ],
    [
        'Toyota Supra (JZA80)',
        '1993–2002',
        'The last two years of production are the ones still arriving; 2001 cars clear this year.'
    ],
    [
        'Mazda RX-7 (FD3S)',
        '1991–2002',
        '2001 builds clear in 2026; the final Spirit R is a 2002 car.'
    ],
    [
        'Mitsubishi Lancer Evolution VII (CT9A)',
        'from 2001',
        'Launched early in 2001, so early cars are already eligible as their months come round.'
    ],
    [
        'Honda Integra Type R (DC5)',
        'from 2001',
        'Production started in the second half of 2001, so the earliest cars clear late in 2026: check each car’s build month.'
    ],
    [
        'Nissan Silvia (S15)',
        '1999–2002',
        '2001 builds clear in 2026.'
    ],
    [
        'Subaru Impreza WRX STI (GDB)',
        'from 2000',
        'Early “bugeye” cars built in 2001 clear in 2026.'
    ],
    [
        'Toyota Mark II (JZX110)',
        '2000–2004',
        '2001 builds of the turbocharged iR-V clear in 2026.'
    ]
];
export default function TwentyFiveYearRule() {
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("p", {
                children: "Most JDM cars were never built to US safety and emissions standards, and the reason you can import them anyway is age. The 25-year rule is the exemption every JDM import business runs on, and in 2026 it is working its way through model year 2001, one month at a time. Here is how it is counted, what proves it, and which cars are crossing the line."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "How the 25-year rule works"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "US safety rules (the FMVSS, enforced by NHTSA) don’t apply to a vehicle that is at least 25 years old. You claim that on the DOT HS-7 form when the car is imported. On the emissions side, EPA excludes vehicles that are at least 21 years old and in their original configuration, claimed on EPA form 3520-1. Because the safety exemption takes longer, 25 years is the number that decides when a JDM car can come in for road use."
            }),
            /*#__PURE__*/ _jsx("blockquote", {
                children: "This is general information, not legal advice. Your customs broker files the entry and decides what documentation it needs; ask them before you bid on a car that’s close to the line."
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "It’s the month, not the year"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "The 25 years are counted from the date of manufacture, to the month. A car built in March 2001 can be imported from March 2026; one built in November 2001 has to wait until November 2026. So “2001 cars are legal in 2026” is only true by the end of the year."
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "That date isn’t always easy to find. The year on an auction sheet is normally the first registration year, in the Japanese era (H13 is 2001; see",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: "/blog/how-to-read-a-japanese-auction-sheet",
                        children: "how to read a Japanese auction sheet"
                    }),
                    "). Japanese cars carry a frame number rather than a 17-digit VIN, and the build month usually comes from a production-date lookup on that frame number or from the car itself. Get it in writing before the car ships: a car that lands a month early can sit at the port, or be refused."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "The paperwork that claims it"
            }),
            /*#__PURE__*/ _jsxs("ul", {
                children: [
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "DOT HS-7"
                            }),
                            ", declaring the vehicle is at least 25 years old."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            /*#__PURE__*/ _jsx("strong", {
                                children: "EPA 3520-1"
                            }),
                            ", declaring the age exclusion for an original-configuration vehicle."
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("li", {
                        children: [
                            "The ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "export certificate"
                            }),
                            " from Japan (and an English translation), the",
                            ' ',
                            /*#__PURE__*/ _jsx("strong", {
                                children: "bill of lading"
                            }),
                            " and the agent’s ",
                            /*#__PURE__*/ _jsx("strong", {
                                children: "commercial invoice"
                            }),
                            ", all showing the same frame number."
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "The ",
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.guide,
                        children: "importing guide"
                    }),
                    " walks through the entry itself: the broker, CBP form 7501, duty and fees."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "JDM cars that clear in 2026"
            }),
            /*#__PURE__*/ _jsx("p", {
                children: "These are the 2001 cars shops are watching. Production years are for the Japanese market; always check the individual car’s build month."
            }),
            /*#__PURE__*/ _jsx("div", {
                className: "mk-post__table",
                children: /*#__PURE__*/ _jsxs("table", {
                    children: [
                        /*#__PURE__*/ _jsx("thead", {
                            children: /*#__PURE__*/ _jsxs("tr", {
                                children: [
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Car"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "Built"
                                    }),
                                    /*#__PURE__*/ _jsx("th", {
                                        children: "In 2026"
                                    })
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsx("tbody", {
                            children: WATCH.map(([car, built, note])=>/*#__PURE__*/ _jsxs("tr", {
                                    children: [
                                        /*#__PURE__*/ _jsx("td", {
                                            children: /*#__PURE__*/ _jsx("strong", {
                                                children: car
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: built
                                        }),
                                        /*#__PURE__*/ _jsx("td", {
                                            children: note
                                        })
                                    ]
                                }, car))
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Kei cars and trucks from 2001 clear on the same schedule, but federal eligibility is only half of it for kei vehicles: states set their own rules for road use. See",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: "/blog/importing-a-kei-truck",
                        children: "importing a kei truck"
                    }),
                    "."
                ]
            }),
            /*#__PURE__*/ _jsx("h2", {
                children: "Buying ahead of the date"
            }),
            /*#__PURE__*/ _jsxs("p", {
                children: [
                    "Many shops buy a car a few months before its anniversary so it arrives just after. That works if you know the build month and your agent can hold or time the shipment. Right Hand DMS’s auction search defaults to importable model years and converts the era year on every sheet it reads, and a hunt can wait on a model until its year comes up: see",
                    ' ',
                    /*#__PURE__*/ _jsx("a", {
                        href: MKT.product('hunts'),
                        children: "hunts"
                    }),
                    "."
                ]
            })
        ]
    });
}
